// Logique d'abonnement côté client.
//
// À partir du `tier` et de la fin d'essai (`trialEndsAt`) déjà présents sur le
// compte connecté, on déduit le statut d'accès, les jours d'essai restants et la
// formule en cours. La SOURCE DE VÉRITÉ des prix, quotas et durée d'essai reste
// shared/constants.ts (réexportés ici pour un import unique côté UI).
//
// Cette brique est purement informative (affichage). Le paiement (Stripe) et le
// blocage en fin d'essai viendront dans des briques séparées.

import { PRICING, CLAUDE_QUOTAS, TRIAL_DURATION_DAYS } from '@shared/constants'
import { accesActif } from '@shared/calculs/abonnement'

export type TierUtilisateur = 'trial' | 'starter' | 'premium'

// 'acces_expire' = compte payant dont l'accès est échu (abonnement résilié/expiré OU compte
// offert dont la période est passée). Distinct de 'essai_expire' pour ne pas afficher « essai
// terminé » à une ex-abonnée.
export type StatutAbonnement = 'essai_actif' | 'essai_expire' | 'abonne' | 'acces_expire'

export interface InfoAbonnement {
  statut: StatutAbonnement
  // Jours d'essai restants (entier ≥ 0). Pertinent uniquement pendant l'essai.
  joursEssaiRestants: number
  // Libellé lisible de la formule en cours.
  formuleLabel: string
}

// Jours pleins restants jusqu'à `fin` (arrondi au supérieur), 0 si la date est passée.
function joursRestants(fin: Date, maintenant: Date): number {
  const ms = fin.getTime() - maintenant.getTime()
  if (ms <= 0) return 0
  return Math.ceil(ms / (24 * 60 * 60 * 1000))
}

// Déduit l'état d'abonnement d'un compte. `maintenant` est injectable pour les tests.
// La DÉCISION D'ACCÈS est déléguée à `accesActif` (shared) : même règle que le mur S3, pour
// qu'un compte payant résilié/expiré ne soit jamais présenté comme « abonné ». Les champs
// Stripe sont optionnels (rétro-compatibilité des appels et des tests existants).
export function calculerAbonnement(
  user: {
    tier: TierUtilisateur
    trialEndsAt: string | Date | null
    role?: 'user' | 'admin'
    subscriptionStatus?: string | null
    currentPeriodEnd?: string | Date | null
  },
  maintenant: Date = new Date(),
): InfoAbonnement {
  const actif = accesActif(
    {
      role: user.role ?? 'user',
      tier: user.tier,
      trialEndsAt: user.trialEndsAt,
      subscriptionStatus: user.subscriptionStatus ?? null,
      currentPeriodEnd: user.currentPeriodEnd ?? null,
    },
    maintenant,
  )

  // Compte payant : abonnement Stripe en cours, résiliation encore couverte, ou compte offert
  // valide → « abonnée » ; sinon accès échu → réabonnement.
  if (user.tier === 'starter' || user.tier === 'premium') {
    return {
      statut: actif ? 'abonne' : 'acces_expire',
      joursEssaiRestants: 0,
      formuleLabel: PRICING[user.tier].label,
    }
  }

  // tier === 'trial' : l'accès dépend de la date de fin d'essai.
  const fin = user.trialEndsAt ? new Date(user.trialEndsAt) : null
  const restant = fin ? joursRestants(fin, maintenant) : 0
  return {
    statut: actif ? 'essai_actif' : 'essai_expire',
    joursEssaiRestants: restant,
    formuleLabel: 'Essai gratuit',
  }
}

// Message à afficher au retour de Stripe, lu depuis le paramètre ?abonnement=succes|annule
// (renvoyé par les success_url / cancel_url du Checkout). Fonction pure → testable sans DOM.
export interface RetourStripe {
  ton: 'succes' | 'annule'
  texte: string
}

export function messageRetourStripe(search: string): RetourStripe | null {
  const valeur = new URLSearchParams(search).get('abonnement')
  if (valeur === 'succes') {
    return { ton: 'succes', texte: 'Paiement confirmé. Ton abonnement est actif, merci !' }
  }
  if (valeur === 'annule') {
    return { ton: 'annule', texte: 'Paiement annulé. Aucun montant n’a été prélevé.' }
  }
  return null
}

export { PRICING, CLAUDE_QUOTAS, TRIAL_DURATION_DAYS }
